import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, ChevronRight, Users } from 'lucide-react';
import { companyInfo, contactInfo, services, navLinks } from '../../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [visitorCount, setVisitorCount] = useState(0);

  useEffect(() => {
    const trackVisitor = async () => {
      try {
        // Check if this session has already been counted
        const hasVisited = sessionStorage.getItem('sragcircle_visited');
        
        if (!hasVisited) {
          // Increment visitor count for new session
          const response = await axios.post(`${BACKEND_URL}/api/visitor`);
          setVisitorCount(response.data.count);
          sessionStorage.setItem('sragcircle_visited', 'true');
        } else {
          // Just get the current count
          const response = await axios.get(`${BACKEND_URL}/api/visitor`);
          setVisitorCount(response.data.count);
        }
      } catch (error) {
        console.error('Error tracking visitor:', error);
        // Try to get count even if increment fails
        try {
          const response = await axios.get(`${BACKEND_URL}/api/visitor`);
          setVisitorCount(response.data.count);
        } catch (e) {
          console.error('Error getting visitor count:', e);
        }
      }
    };

    trackVisitor();
  }, []);

  return (
    <footer className="bg-[#0a0a1a] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="flex items-center gap-2 text-gray-400 hover:text-[#00C0FF] transition-colors text-sm group"
                  >
                    <ChevronRight className="w-4 h-4 text-[#0066CC] transition-transform group-hover:translate-x-1" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products & Services - Spans 2 columns */}
          <div className="lg:col-span-2">
            <Link to="/services" className="text-white font-semibold text-lg mb-6 block hover:text-[#00C0FF] transition-colors">
              Our Products & Services
            </Link>
            <div className="grid grid-cols-2 gap-x-8">
              {/* Left Column */}
              <ul className="space-y-3">
                {services.slice(0, 5).map((service) => (
                  <li key={service.id}>
                    <div
                      className="flex items-center gap-1 text-gray-400 hover:text-[#00C0FF] transition-colors text-sm group cursor-default"
                    >
                      <ChevronRight className="w-3 h-3 flex-shrink-0 text-[#0066CC] transition-transform group-hover:translate-x-1" />
                      <span className="leading-tight">{service.title}</span>
                    </div>
                  </li>
                ))}
              </ul>
              {/* Right Column */}
              <ul className="space-y-3">
                {services.slice(5).map((service) => (
                  <li key={service.id}>
                    <div
                      className="flex items-center gap-1 text-gray-400 hover:text-[#00C0FF] transition-colors text-sm group cursor-default"
                    >
                      <ChevronRight className="w-3 h-3 flex-shrink-0 text-[#0066CC] transition-transform group-hover:translate-x-1" />
                      <span className="leading-tight">{service.title}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Us */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">Contact Us</h3>
            <div className="space-y-4">
              <div className="text-sm text-gray-400 space-y-3">
                <a 
                  href={`tel:${contactInfo.mobile}`}
                  className="flex items-center gap-2 hover:text-[#00C0FF] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#0066CC]" />
                  {contactInfo.mobile}
                </a>
                <a 
                  href={`tel:${contactInfo.officePhone}`}
                  className="flex items-center gap-2 hover:text-[#00C0FF] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#0066CC]" />
                  {contactInfo.officePhone}
                </a>
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-2 hover:text-[#00C0FF] transition-colors"
                >
                  <Mail className="w-4 h-4 text-[#0066CC]" />
                  {contactInfo.email}
                </a>
              </div>
              
              {/* Visitor Counter */}
              <div className="pt-4 border-t border-gray-800">
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <Users className="w-4 h-4 text-[#0066CC]" />
                  <span>
                    Visitors: <span className="text-[#00C0FF] font-semibold">{visitorCount.toLocaleString()}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} {companyInfo.name}. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/terms" className="text-gray-500 hover:text-[#00C0FF] transition-colors">
                Terms of Use
              </Link>
              <span className="text-gray-700">|</span>
              <Link to="/privacy" className="text-gray-500 hover:text-[#00C0FF] transition-colors">
                Privacy Policy
              </Link>
              <span className="text-gray-700">|</span>
              <Link to="/copyright" className="text-gray-500 hover:text-[#00C0FF] transition-colors">
                Copyright & IP
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
